import React, { useState, useEffect } from "react";
import { Dialog, Flex, Text, TextField, Button, Progress, Callout, Checkbox } from "@radix-ui/themes";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/solid";
import { useFeatureFlagProduct } from "../../../hooks/billing/useBilling";

export function AuthMetaDialog({ locationsData, allLocationsData, brandsData, isOpen, onOpenChange, onSave, entityConfigs, setEntityConfigs, isLoading, progressValue, title = "Add Meta Pixel/Dataset Account" }) {
  const [billingAccepted, setBillingAccepted] = useState(false);
  
  // Get Meta Ads feature flag product details
  const { data: metaProductData, isLoading: isProductLoading, error: productError } = useFeatureFlagProduct("meta_ads_feature_flag");
  
  // Check if user has configured any Meta integration (for flat-rate billing)
  const hasAnyConfiguration = () => {
    return entityConfigs?.some(entity => 
      entity.accessToken && entity.pixelId
    ) || false;
  };
  
  // Check if user has partial configurations (for warning messages)
  const hasPartialConfiguration = () => {
    return entityConfigs?.some(entity => 
      (entity.accessToken && !entity.pixelId) || (!entity.accessToken && entity.pixelId)
    ) || false;
  };
  
  const hasConfiguration = hasAnyConfiguration();
  const hasPartialConfig = hasPartialConfiguration();
  const monthlyPrice = metaProductData?.data?.price || 0;
  // Flat-rate billing: either 0 (no config) or full price (any config)
  const totalMonthlyCost = hasConfiguration ? monthlyPrice : 0;
  
  // Format currency helper
  const formatCurrency = (amount) => {
    return `£${parseFloat(amount).toFixed(2)}`;
  };
  
  useEffect(() => {
    if (brandsData?.length || locationsData?.length) {
      const configs = [];
      // Non-default brands
      brandsData
        .forEach(b => {
          configs.push({
            id: b.id,
            name: b.name,
            type: "brand",
            accessToken: "",
            pixelId: "",
            pixelId: "",
            isAccessTokenValid: true,
            isPixelIdValid: true,
          });
        });
      // All org locations
      locationsData.forEach(loc => {
        configs.push({
          id: loc.id,
          name: loc.name,
          type: "location",
          accessToken: "",
          pixelId: "",
          isAccessTokenValid: true,
          isPixelIdValid: true,
        });
      });

      setEntityConfigs(configs);
    }
  }, [brandsData, locationsData]);

  // Reset billing acceptance when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setBillingAccepted(false);
    }
  }, [isOpen]);

  return (
    entityConfigs?.length > 0 ? (
      <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
        <Dialog.Content maxWidth="600px" maxHeight="690px" align="center">
          <Dialog.Title>{title}</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            To find your API access token, log in to Meta Business Manager and go to Events Manager.

            <Text style={{ padding: "0px var(--space-10)" }} size="1" weight="regular">
              <ul style={{ listStyleType: "disc", alignItems: "center", padding: "0.5rem 1rem"}}>
                <li>Click 'Connect data' and then select 'Offline'.</li>
                <li>Add the or create correct dataset ID (formerly known as Pixel ID).</li>
                <li>Select 'other ways to connect' and then select manually (this will allow you to create your access token).</li>
                <li>Select to have 'Conversions API and Meta Pixel'.</li>
                <li>Go to your 'datasets' and find the correct one you setup (or already had) for this the purposes of your meta integration, and go to the 'settings' tab.</li>
                <li>Scroll until you find 'Generate access token' and create one.</li>
              </ul>
            </Text>
            
            If you require additonal support, please contact the b connect team at support@b-connect.co.uk.
          </Dialog.Description>

          {entityConfigs.map((entity, index) => (
            <Flex
              key={`${entity.type}-${entity.id}`}
              direction="row"
              justify="between"
              style={{ width: "100%", marginBottom: "8px" }}
            >
              {/* Left: Name Box */}
              <Flex style={{ width: "32%", minWidth: 0 }}>
                <Flex
                  style={{
                    width: "100%",
                    backgroundColor: "var(--gray-3)",
                    border: "1px solid var(--gray-a7)",
                    borderRadius: "var(--radius-2)",
                    padding: "0 var(--space-2)",
                    height: "34px",
                    alignItems: "center",
                    overflow: "hidden",
                  }}
                >
                  <Text
                    size="2"
                    title={entity.name}
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      width: "100%",
                      color: "var(--gray-a11)",
                    }}
                  >
                    {entity.name}
                  </Text>
                </Flex>
              </Flex>

              {/* Middle: Access Token Input */}
              <Flex style={{ width: "32%", minWidth: 0, paddingLeft: "8px", paddingRight: "8px" }}>
                <TextField.Root
                  placeholder="Access Token"
                  value={entity.accessToken}
                  onChange={(e) =>
                    setEntityConfigs((prev) =>
                      prev.map((config, idx) =>
                        idx === index
                          ? { ...config, accessToken: e.target.value, isAccessTokenValid: true }
                          : config
                      )
                    )
                  }
                  size="2"
                  variant="surface"
                  className={entity.isAccessTokenValid ? "" : "border border-red-500"}
                  style={{
                    width: "100%",
                    height: "34px",
                    borderRadius: "var(--radius-2)",
                  }}
                />
              </Flex>

              {/* Right: Pixel ID Input */}
              <Flex style={{ width: "32%", minWidth: 0 }}>
                <TextField.Root
                  placeholder="Pixel ID"
                  value={entity.pixelId}
                  onChange={(e) =>
                    setEntityConfigs((prev) =>
                      prev.map((config, idx) =>
                        idx === index
                          ? { ...config, pixelId: e.target.value, isPixelIdValid: true }
                          : config
                      )
                    )
                  }
                  size="2"
                  className={entity.isPixelIdValid ? "" : "border border-red-500"}
                  variant="surface"
                  style={{
                    width: "100%",
                    height: "34px",
                    borderRadius: "var(--radius-2)",
                  }}
                />
              </Flex>
            </Flex>
          ))}

          {/* Billing Confirmation Section */}
          {entityConfigs?.length > 0 && (
            <Flex
              direction="column"
              gap="3"
              p="3"
              mt="4"
              style={{
                border: "1px solid var(--orange-a7)",
                borderRadius: "var(--radius-3)",
                backgroundColor: "var(--orange-a2)",
                width: "100%"
              }}
            >
              <Text size="3" weight="medium" color="orange">
                Billing Information
              </Text>
              
              {hasConfiguration ? (
                <>
                  <Flex direction="column" gap="2">
                    <Flex justify="between" align="center">
                      <Text size="2">
                        Meta Ads integration
                      </Text>
                      <Text size="2" weight="medium">
                        {formatCurrency(monthlyPrice)}/month
                      </Text>
                    </Flex>
                  </Flex>

                  <Flex align="center" gap="2" mt="2">
                    <Checkbox
                      checked={billingAccepted}
                      onCheckedChange={setBillingAccepted}
                      size="2"
                    />
                    <Text size="2">
                      I understand and accept the monthly billing charge of {formatCurrency(monthlyPrice)} for Meta Ads integration
                    </Text>
                  </Flex>
                </>
              ) : (
                <>
                  <Text size="2" color="gray">
                    No billing charges will apply until you configure both access token and pixel ID:
                  </Text>
                  <Flex direction="column" gap="1">
                    <Text size="2" color="gray">
                      • Meta Ads uses billing: {formatCurrency(monthlyPrice)}/month
                    </Text>
                    {hasPartialConfig && (
                      <Text size="2" color="amber" style={{ fontStyle: "italic" }}>
                        • Some entities are partially configured (need both token and pixel)
                      </Text>
                    )}
                  </Flex>
                </>
              )}

              {productError && (
                <Text size="2" color="red">
                  Unable to load pricing information. Please try again.
                </Text>
              )}

              {isProductLoading && (
                <Text size="2" color="gray">
                  Loading pricing information...
                </Text>
              )}
            </Flex>
          )}

          <Flex
            style={{
              alignSelf: "stretch",
              width: "100%",
            }}
            display="flex"
            justify="between"
            align="center"
            mt="4"
          >
            <Button size="2" variant="ghost" color="gray" onClick={() => window.open("https://business.facebook.com/", "_blank")}>
              <Flex display="flex" align="center" gap="2">
                Open Meta Business Manager
                <ArrowTopRightOnSquareIcon style={{ width: "16px", height: "16px" }} />
              </Flex>
            </Button>

            <Flex display="flex" gap="3" justify="end">
              <Dialog.Close>
                <Button size="2" variant="soft" color="gray">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button 
                onClick={onSave} 
                size="2" 
                variant="soft" 
                color="accent" 
                loading={isLoading}
                disabled={hasConfiguration && !billingAccepted}
              >
                Save
              </Button>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    ) : (
      <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
        <Dialog.Content maxWidth="600px" maxHeight="690px" align="center">
          <Dialog.Title>{title}</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            To find your API access token, log in to Meta Business Manager and go to Events Manager.

            <Text style={{ padding: "0px var(--space-10)" }} size="1" weight="regular">
              <ul style={{ listStyleType: "disc", alignItems: "center", padding: "0.5rem 1rem"}}>
                <li>Click 'Connect data' and then select 'Offline'.</li>
                <li>Add the or create correct dataset ID (formerly known as Pixel ID).</li>
                <li>Select 'other ways to connect' and then select manually (this will allow you to create your access token).</li>
                <li>Select to have 'Conversions API and Meta Pixel'.</li>
                <li>Go to your 'datasets' and find the correct one you setup (or already had) for this the purposes of your meta integration, and go to the 'settings' tab.</li>
                <li>Scroll until you find 'Generate access token' and create one.</li>
              </ul>
            </Text>

            If you require additonal support, please contact the b connect team at support@b-connect.co.uk.
          </Dialog.Description>

          <Callout.Root color="yellow">
            <Callout.Icon>
              <InfoCircledIcon />
            </Callout.Icon>
            <Callout.Text>
              Currently no brands or locations to integrate with Meta. Please create at least one of them to get started.
            </Callout.Text>
          </Callout.Root>

          <Flex
            style={{
              alignSelf: "stretch",
              width: "100%",
            }}
            display="flex"
            justify="between"
            align="center"
            mt="4"
          >
            <Button size="2" variant="ghost" color="gray" onClick={() => window.open("https://business.facebook.com/", "_blank")}>
              <Flex display="flex" align="center" gap="2">
                Open Meta Business Manager
                <ArrowTopRightOnSquareIcon style={{ width: "16px", height: "16px" }} />
              </Flex>
            </Button>

            <Flex display="flex" gap="3" justify="end">
              <Dialog.Close>
                <Button size="2" variant="soft" color="gray">
                  Cancel
                </Button>
              </Dialog.Close>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    )
  );
}
